import { Loader2 } from "lucide-react";
import type { QuizQuestion } from "@/types/quiz";
import type {
  ScoringPreviewResponse,
  ScoringPreviewProduct,
} from "@/types/scoring-rules";

interface TopMatchesListProps {
  data: ScoringPreviewResponse | null;
  isLoading: boolean;
  hasSelections: boolean;
  questions: QuizQuestion[];
}

function resolveLabel(
  questions: QuizQuestion[],
  questionKey: string,
  answerId: string,
): { questionText: string; answerText: string } {
  const question = questions.find((q) => q.questionKey === questionKey);
  const answer = question?.options?.find(
    (o) => o.id === answerId || o.value === answerId,
  );
  return {
    questionText: question?.questionText ?? questionKey,
    answerText: answer?.text ?? answerId,
  };
}

const priceFormat = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});

/**
 * Budget filter banner shown above the match list (S-40 PR 4c).
 * Mirrors the widget FilterHint wording for Glass-Box parity.
 */
function FilterHint({
  appliedConstraint,
  relaxed,
}: {
  appliedConstraint?: ScoringPreviewResponse["appliedConstraint"];
  relaxed?: boolean;
}) {
  if (appliedConstraint == null) return null;

  const priceMax = priceFormat.format(appliedConstraint.priceMax);
  const text = relaxed
    ? `Keine Produkte bis ${priceMax} gefunden – wir zeigen dir die gesamte Auswahl.`
    : `Gefiltert auf dein Budget bis ${priceMax}`;

  return (
    <p className="border-b bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
      {text}
    </p>
  );
}

export function TopMatchesList({
  data,
  isLoading,
  hasSelections,
  questions,
}: TopMatchesListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-background p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Treffer werden berechnet …
      </div>
    );
  }

  if (!hasSelections || !data) {
    return (
      <div className="rounded-md border border-dashed bg-background p-6 text-center text-sm text-muted-foreground">
        Wähle Antworten oben, um die Treffer zu sehen.
      </div>
    );
  }

  if (data.products.length === 0) {
    return (
      <div className="rounded-md border border-dashed bg-background p-6 text-center text-sm text-muted-foreground">
        Keine Produkte mit passenden Tags gefunden.
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border bg-background">
      <FilterHint
        appliedConstraint={data.appliedConstraint}
        relaxed={data.relaxed}
      />
      <ul className="divide-y">
        {data.products.map((product, idx) => (
          <ProductRow
            key={product.id}
            product={product}
            rank={idx + 1}
            questions={questions}
          />
        ))}
      </ul>
      <p className="px-4 pb-3 text-xs text-muted-foreground">
        {data.totalRulesApplied}{" "}
        {data.totalRulesApplied === 1 ? "Regel" : "Regeln"} angewendet ·{" "}
        {Object.keys(data.tagSummary).length} Tags aktiv
      </p>
    </div>
  );
}

function ProductRow({
  product,
  rank,
  questions,
}: {
  product: ScoringPreviewProduct;
  rank: number;
  questions: QuizQuestion[];
}) {
  // S-44 PR-3: nicht erfüllte Kriterien filtern + deduplizieren — ZEICHENGLEICH
  // zu MatchReasons in ResultsView.tsx (S-44 PR-2), damit Admin == Widget (S-22).
  const unmetCriteria = product.unmetCriteria ?? [];
  const unmet = [
    ...new Set(unmetCriteria.filter((t) => !!t && t.trim().length > 0)),
  ];

  return (
    <li className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-muted-foreground">
            #{rank}
          </span>
          <p className="text-sm font-medium">{product.name}</p>
        </div>
        <span className="text-sm font-semibold">
          Score: {product.totalScore}
        </span>
      </div>
      {/* S-44 PR-3: rang-basiertes Label + Deckungsgrad, defensiv (Empty-Rules-
          Zweig liefert keine Signal-Felder). Spiegelbildlich zu ResultsView.tsx. */}
      {product.matchLabel && (
        <div className="mt-1.5 inline-flex items-center rounded-full border bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
          {product.matchLabel}
        </div>
      )}
      {product.coverage && (
        <p className="mt-1 text-xs text-muted-foreground">
          {/* S-22-Wort-Parität — Kanon: ResultsView.tsx. Bei Textänderung dort UND hier angleichen. */}
          Passt zu {product.coverage.matched} von{" "}
          {product.coverage.applicable} Kriterien
        </p>
      )}
      {product.matchedTags.length > 0 && (
        <ul className="mt-2 space-y-1">
          {product.matchedTags.map((tag, i) => {
            const labels = resolveLabel(
              questions,
              tag.fromQuestion,
              tag.fromAnswer,
            );
            return (
              <li
                key={`${tag.tag}-${i}`}
                className="rounded border bg-muted/30 px-2 py-1.5 text-xs"
              >
                <p>
                  <span className="font-medium">{labels.questionText}</span>
                  {" → "}
                  <span>{labels.answerText}</span>
                </p>
                <p className="text-muted-foreground">
                  Tag: {tag.tag} · Gewicht: {tag.weight >= 0 ? "+" : ""}
                  {tag.weight}
                </p>
                {tag.reasonText && (
                  <p className="italic text-muted-foreground">
                    {tag.reasonText}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {unmet.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground">
            Nicht erfüllt:
          </p>
          <ul className="mt-1 space-y-1">
            {unmet.map((text) => (
              <li
                key={text}
                className="rounded border border-dashed px-2 py-1.5 text-xs text-muted-foreground"
              >
                {text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}
